// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * Path handling for ZIP extraction.
 *
 * The security-critical part of the app: archive entry names are attacker
 * controlled, so they must be sanitised before they are ever used to write a
 * file. Defends against Zip-Slip (https://security.snyk.io/research/zip-slip-vulnerability)
 * — entries like `../../etc/passwd` or `C:\Windows\evil.dll` that try to escape
 * the chosen output directory.
 */

import type { TreeNode, ZipEntry } from './types';

// Built from a string literal (not a regex literal) so no raw control bytes
// live in this source file: matches 0x00–0x1F and 0x7F.
const CONTROL_CHARS = new RegExp('[\\x00-\\x1f\\x7f]', 'g');

/**
 * Normalise and sanitise a raw ZIP entry path into a safe relative path.
 *
 * - Backslashes become forward slashes (Windows-authored zips).
 * - Drive letters (`C:`) and leading slashes are stripped (no absolute paths).
 * - `.` and `..` segments are removed so nothing can climb out of the target dir.
 * - Control characters are stripped.
 *
 * Returns an empty string if nothing safe remains (caller should skip the entry
 * or substitute a fallback name).
 */
export function sanitizeZipPath(raw: string): string {
  if (!raw) return '';
  // Normalise separators and strip a Windows drive prefix like "C:".
  let p = raw.replace(/\\/g, '/').replace(/^[a-zA-Z]:/, '');
  // Strip control characters that have no business in a filename.
  p = p.replace(CONTROL_CHARS, '');

  const safeSegments: string[] = [];
  for (const segment of p.split('/')) {
    if (segment === '' || segment === '.') continue;
    if (segment === '..') continue; // never allow climbing up
    // Trim trailing dots/spaces (Windows would silently drop them).
    const cleaned = segment.replace(/[. ]+$/, '');
    if (cleaned === '') continue;
    safeSegments.push(cleaned);
  }
  return safeSegments.join('/');
}

/**
 * True if a raw entry path would have escaped the target directory — i.e. it
 * contains a `..` traversal segment, an absolute root, or a Windows drive letter.
 */
export function isUnsafePath(raw: string): boolean {
  if (!raw) return false;
  const normalised = raw.replace(/\\/g, '/');
  if (/^[a-zA-Z]:/.test(normalised)) return true; // drive letter
  if (normalised.startsWith('/')) return true; // absolute
  return normalised.split('/').some((seg) => seg === '..');
}

/** Map a flat list of entries into a nested tree for display. */
export function buildTree(entries: ZipEntry[]): TreeNode {
  const root: TreeNode = { name: '', path: '', isDir: true, size: 0, children: [] };

  for (const entry of entries) {
    const parts = entry.path.split('/').filter(Boolean);
    if (parts.length === 0) continue;
    let node = root;
    let acc = '';
    for (let i = 0; i < parts.length; i++) {
      const part = parts[i];
      acc = acc ? `${acc}/${part}` : part;
      const isLeaf = i === parts.length - 1;
      let child = node.children.find((c) => c.name === part);
      if (!child) {
        child = {
          name: part,
          path: acc,
          isDir: isLeaf ? entry.isDir : true,
          size: 0,
          children: [],
        };
        node.children.push(child);
      }
      if (isLeaf && !entry.isDir) {
        child.isDir = false;
        child.size = entry.size;
      }
      node = child;
    }
  }

  sortAndSum(root);
  return root;
}

/** Recursively sort children (dirs first, then alpha) and roll up sizes. */
function sortAndSum(node: TreeNode): number {
  if (!node.isDir) return node.size;
  let total = 0;
  for (const child of node.children) total += sortAndSum(child);
  node.size = total;
  node.children.sort((a, b) => {
    if (a.isDir !== b.isDir) return a.isDir ? -1 : 1;
    return a.name.localeCompare(b.name);
  });
  return total;
}

/** Count the file (non-dir) leaves in a tree. */
export function countFiles(node: TreeNode): number {
  if (!node.isDir) return 1;
  return node.children.reduce((n, c) => n + countFiles(c), 0);
}
